// Run work over a list a chunk at a time instead of all at once.
//
// Follows batchProcess() in v2_firebase_functions
// (functions/services/targeting_service/_shared.js), including its default of
// 50 concurrent operations and its {results, failures} return.
//
// Send Rewards posts once per fan to the reward service, which sends through
// Mailgun. Promise.all over a full check-in list fires every request in the
// same tick — a few hundred at a burst against a service with rate limits, and
// no sign of progress while it happens.
//
// Note this limits CONCURRENCY, not Mailgun's per-call recipient count (1,000 —
// MAILGUN_BATCH_SIZE in v2): each request here still carries one fan.
// Collapsing them into one multi-recipient call the way v2's blast path does
// would be better still, but that is a change to the reward service, not to
// this admin.

export const DEFAULT_BATCH_SIZE = 50;

/**
 * @param items       the things to work through
 * @param worker      (item, index) => Promise<result>
 * @param size        how many run at once
 * @param onProgress  (done, total) after each chunk, for a live count
 * @returns { results, failures } — failures carry {index, item, error} so a
 *          fan who did not get their reward can still be named. allSettled,
 *          not all: one thrown error must not abandon every fan after it.
 */
// A worker that throws synchronously — before returning its promise — would
// escape Promise.allSettled and reject the whole run, which is the very thing
// allSettled is here to prevent. Turn it into a rejection like any other.
function invoke(worker, item, index) {
    try {
        return Promise.resolve(worker(item, index));
    } catch (e) {
        return Promise.reject(e);
    }
}

export async function runInBatches(items, worker, { size = DEFAULT_BATCH_SIZE, onProgress } = {}) {
    const list = items || [];
    const results = [];
    const failures = [];
    // A size of 0 or less would advance the loop by nothing and hang the tab.
    const batchSize = Math.max(1, size);
    let done = 0;

    for (let i = 0; i < list.length; i += batchSize) {
        const batch = list.slice(i, i + batchSize);
        // allSettled within a chunk only — the next chunk waits for this one,
        // which is what keeps the burst bounded.
        const batchResults = await Promise.allSettled(batch.map((item, idx) => invoke(worker, item, i + idx)));

        batchResults.forEach((result, idx) => {
            if (result.status === 'fulfilled') {
                results.push(result.value);
            } else {
                failures.push({
                    index: i + idx,
                    item: batch[idx],
                    error: (result.reason && result.reason.message) || 'Unknown error'
                });
            }
        });

        done += batch.length;
        if (onProgress) onProgress(done, list.length);
    }

    return { results, failures };
}
